import { useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg, BACKEND } from "@/lib/api";
import { Card } from "./BookingsTab";

export const SyncTab = ({ prop, onSaved }) => {
  const [url, setUrl] = useState(prop.ical_import_url || "");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(prop.ical_token);
  const exportUrl = `${BACKEND}/api/ical/${prop.slug}.ics?token=${token}`;
  const saveUrl = async () => {
    try {
      const { data } = await api.put(`/admin/properties/${prop.id}`, { ical_import_url: url.trim() });
      onSaved(data);
      toast.success("Enlace guardado");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const sync = async () => {
    setBusy(true);
    try {
      if (url.trim() !== (prop.ical_import_url || "") && !(await saveUrl())) return;
      const { data } = await api.post(`/admin/properties/${prop.id}/ical/sync`);
      toast.success(`Sincronizado: ${data.events} reservas/bloqueos de Airbnb`);
      const { data: all } = await api.get("/admin/properties");
      const p = (all || []).find((x) => x.id === prop.id);
      if (p) onSaved(p);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const regen = async () => {
    if (!window.confirm("El enlace anterior dejará de funcionar. Tendrás que actualizarlo en Airbnb. ¿Continuar?")) return;
    const { data } = await api.post(`/admin/properties/${prop.id}/ical/regenerate`);
    setToken(data.ical_token);
    onSaved({ ...prop, ical_token: data.ical_token });
  };
  const copy = async () => { await navigator.clipboard.writeText(exportUrl); toast.success("Enlace copiado"); };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="admin-ical">
      <Card className="space-y-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A2B]">Paso 1 · Airbnb → este sitio</p>
        <p className="font-display text-2xl">Importar calendario de Airbnb</p>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-[#3b2c1f]">
          <li>En Airbnb abre tu anuncio → Calendario → Disponibilidad → Conectar calendarios.</li>
          <li>Elige “Exportar calendario” y copia el enlace .ics.</li>
          <li>Pégalo aquí y pulsa Sincronizar. Se actualiza solo cada 15 minutos.</li>
        </ol>
        <Input data-testid="ical-import-input" placeholder="https://www.airbnb.com/calendar/ical/....ics" value={url} onChange={(e) => setUrl(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <button data-testid="ical-save-button" onClick={saveUrl} className="rounded-lg border border-[#2C2016]/20 px-4 py-2 text-sm font-semibold">Guardar enlace</button>
          <button data-testid="admin-sync-ical-button" disabled={busy || !url.trim()} onClick={sync} className="flex items-center gap-2 rounded-lg bg-[#2C2016] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Sincronizar ahora</button>
        </div>
        <p data-testid="ical-sync-status" className="text-xs text-[#4A3525]">Última sincronización: {prop.last_synced_at ? new Date(prop.last_synced_at).toLocaleString() : "nunca"} {prop.last_sync_status ? `· ${prop.last_sync_status}` : ""}</p>
      </Card>
      <Card className="space-y-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A2B]">Paso 2 · Este sitio → Airbnb</p>
        <p className="font-display text-2xl">Exportar a Airbnb</p>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-[#3b2c1f]">
          <li>En Airbnb → Conectar calendarios → “Importar calendario”.</li>
          <li>Pega este enlace y ponle nombre “SamaraBeachHouse.com”.</li>
          <li>Airbnb bloqueará las fechas reservadas aquí (pendientes, confirmadas y bloqueos manuales).</li>
        </ol>
        <div className="flex gap-2">
          <Input data-testid="ical-export-url" readOnly value={exportUrl} className="text-xs" />
          <button data-testid="ical-copy-button" onClick={copy} className="rounded-lg bg-[#2C2016] px-3 text-white"><Copy className="h-4 w-4" /></button>
        </div>
        <button data-testid="ical-regenerate-button" onClick={regen} className="text-xs underline">Generar un enlace nuevo</button>
        <p className="text-xs text-[#4A3525]">Nota: Airbnb lee calendarios externos aproximadamente cada pocas horas; durante ese intervalo revisa las solicitudes antes de confirmar.</p>
      </Card>
    </div>
  );
};
